import React, { useState } from 'react';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAgentWithSubagents } from '../../hooks/useAgentWithSubagents';
import { useSubscriptionForRenewalDate } from '../../hooks/useSubscriptionForRenewalDate';
import AgentOutputActions from '../../components/Agents/AgentOutputActions';
import { CreditExhaustedModal } from '../../components/Agents/CreditExhaustedModal';
import { CreditUpgradeNudgeModal } from '../../components/Agents/CreditUpgradeNudgeModal';
import SecurityViolationModal from '../../components/Agents/SecurityViolationModal';
import { validateFields } from '../../utils/securityValidator';
import { formatElapsedTime } from '../../utils/timeUtils';
import { 
  AgentPageLayout, 
  AgentFormSection, 
  AgentFormGroup, 
  AgentLabel, 
  AgentInput, 
  AgentButton, 
  AgentError, 
  AgentResult,
  AgentDisclaimer
} from '../../components/Agents/AgentPageLayout';

const PersonalDevotional: React.FC = () => {
  const [biblicalReference, setBiblicalReference] = useState('');
  const [showSecurityModal, setShowSecurityModal] = useState<boolean>(false);
  
  const {
    isLoading,
    error,
    result,
    steps,
    hasStarted,
    elapsedTime,
    execute,
    reset,
    creditsExhausted,
    creditsInfo,
    setCreditsExhausted,
    canRetry,
    onRetry,
    creditUpgradeNudgeOpen,
    creditUpgradeNudgeUsagePercent,
    dismissCreditUpgradeNudge,
  } = useAgentWithSubagents('devocional-pessoal');
  const renewalDate = useSubscriptionForRenewalDate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!biblicalReference.trim()) {
      return;
    }

    // Validar segurança antes de enviar
    if (validateFields(biblicalReference)) {
      setShowSecurityModal(true);
      return;
    }

    await execute({
      prompt: `Crie um guia de devocional pessoal baseado na passagem bíblica: ${biblicalReference}`,
      passagem: biblicalReference
    });
  };

  const handleReset = () => {
    setBiblicalReference('');
    reset();
  };

  return (
    <AgentPageLayout
      icon={<BookOpenIcon className="w-6 h-6 text-secondary" />}
      title="Devocional Pessoal"
      subtitle="Crie um guia devocional que una reflexão bíblica, aplicação prática e crescimento espiritual."
    >
      <AgentFormSection>
        <form onSubmit={handleSubmit} className="space-y-6">
          <AgentFormGroup>
            <AgentLabel htmlFor="biblicalReference">Referência Bíblica</AgentLabel>
            <AgentInput
              id="biblicalReference"
              type="text"
              value={biblicalReference}
              onChange={(e) => setBiblicalReference(e.target.value)}
              placeholder="Ex. Salmo 117; 1 Co 13.13"
              disabled={isLoading}
            />
          </AgentFormGroup>

          <div className="flex justify-end">
            <AgentButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Gerando...</span>
                </>
              ) : (
                <>
                  <BookOpenIcon className="w-5 h-5" />
                  <span>Gerar Devocional</span>
                </>
              )}
            </AgentButton>
          </div>
        </form>

        {error && <AgentError message={error} onRetry={canRetry ? onRetry : undefined} />}
      </AgentFormSection>

      {hasStarted && (
        <section className="mt-8">
          <div className="bg-card border border-border rounded-lg p-6">
            {isLoading && (
              <div className="flex flex-col items-center justify-center gap-4 py-8">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😇</span>
                  <h3 className="text-lg font-semibold text-foreground">
                    Enquanto trabalhamos, eleve seu pensamento e descanse na presença de Deus.
                  </h3>
                </div>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
                {steps.length > 0 && steps[0]?.title && (
                  <p className="text-sm text-muted-foreground">{steps[0].title}</p>
                )}
                {elapsedTime > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Tempo de execução: {formatElapsedTime(elapsedTime)}
                  </p>
                )}
              </div>
            )}
            
            {result && result.trim().length > 0 && (
              <>
                <div className="mt-6">
                  <AgentResult title="Devocional Criado">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {result}
                    </ReactMarkdown>
                  </AgentResult>
                </div>
                <AgentDisclaimer />
                <div className="mt-6 flex items-center justify-end gap-3">
                  <AgentOutputActions
                    content={result}
                    agentName="Devocional Pessoal"
                    agentCode="devocional-pessoal"
                    reference={biblicalReference}
                  />
                  <AgentButton
                    type="button"
                    onClick={handleReset}
                    variant="secondary"
                    aria-label="Reiniciar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                    <span>Reiniciar</span>
                  </AgentButton>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      <SecurityViolationModal
        open={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        onRestart={handleReset}
      />
      <CreditUpgradeNudgeModal
        isOpen={creditUpgradeNudgeOpen}
        onClose={dismissCreditUpgradeNudge}
        usagePercent={creditUpgradeNudgeUsagePercent}
      />
      {creditsExhausted && (
        <CreditExhaustedModal
          isOpen={creditsExhausted}
          onClose={() => setCreditsExhausted(false)}
          renewalDate={renewalDate}
          creditsInfo={creditsInfo || undefined}
        />
      )}
    </AgentPageLayout>
  );
};

export default PersonalDevotional;
